
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Calendar } from '@/components/ui/calendar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { format } from 'date-fns';
import { CalendarDays, Clock } from 'lucide-react';

interface AddSessionFormProps {
  onSubmit: (sessionData: any) => void;
  onCancel: () => void;
  sessionNumber: number;
}

const AddSessionForm: React.FC<AddSessionFormProps> = ({
  onSubmit,
  onCancel,
  sessionNumber
}) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [date, setDate] = useState<Date>(new Date());
  const [hour, setHour] = useState<string>(format(new Date(), 'HH'));
  const [minute, setMinute] = useState<string>('00');
  
  const handleSubmit = () => {
    // Create date with selected time
    const sessionDate = new Date(date);
    sessionDate.setHours(parseInt(hour));
    sessionDate.setMinutes(parseInt(minute));
    
    onSubmit({
      sessionNumber,
      title,
      description,
      date: sessionDate.toISOString(),
      status: 'upcoming'
    });
  };
  
  return (
    <div className="space-y-4 py-4">
      <div className="space-y-2">
        <Label htmlFor="session-title">Session Title*</Label>
        <Input
          id="session-title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g., Initial Consultation, Cleaning, Follow-up"
          required
        />
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="session-description">Description (Optional)</Label>
        <Textarea
          id="session-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Enter a brief description of this session..."
          className="h-20"
        />
      </div>
      
      <div className="space-y-4">
        <div>
          <h3 className="text-sm font-medium mb-2 flex items-center">
            <CalendarDays className="h-4 w-4 mr-2 text-primary" />
            Session Date*
          </h3>
          <Calendar
            mode="single"
            selected={date}
            onSelect={(newDate) => newDate && setDate(newDate)}
            className="rounded-md border"
          />
        </div>
        
        <div>
          <h3 className="text-sm font-medium mb-2 flex items-center">
            <Clock className="h-4 w-4 mr-2 text-primary" />
            Session Time*
          </h3>
          <div className="flex gap-2">
            <div className="w-1/2">
              <Select value={hour} onValueChange={setHour}>
                <SelectTrigger>
                  <SelectValue placeholder="Hour" />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 24 }, (_, i) => (
                    <SelectItem key={i} value={i.toString().padStart(2, '0')}>
                      {i.toString().padStart(2, '0')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-1/2">
              <Select value={minute} onValueChange={setMinute}>
                <SelectTrigger>
                  <SelectValue placeholder="Minute" />
                </SelectTrigger>
                <SelectContent>
                  {['00', '15', '30', '45'].map((min) => (
                    <SelectItem key={min} value={min}>
                      {min}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>
      
      <div className="pt-4 border-t">
        <p className="text-sm mb-4">
          Session #{sessionNumber} - Scheduled for: <span className="font-medium">{format(date, 'EEEE, MMMM d, yyyy')} at {parseInt(hour) > 12 ? parseInt(hour) - 12 : hour}:{minute} {parseInt(hour) >= 12 ? 'PM' : 'AM'}</span>
        </p>
      </div>
      
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={!title}>
          Add Session
        </Button>
      </div>
    </div>
  );
};

export default AddSessionForm;
